import { DrawerState } from 'components/rightDrawer/utils/enums'
import { useRightDrawer } from 'components/rightDrawer/utils/RightDrawerProvider'
import { useRolodexEntries } from 'features/rolodex'
import { useEffect, useMemo, useState } from 'react'
import { RolodexEntryType } from 'types'

/**
 * A hook which uses useRolodexEntries() to load relevant data
 * for the given site and organization IDs.
 * @param props       The current site ID and organization ID
 */
export const useNavbarData = (props: {
  siteID: string | undefined
  orgID?: string
}) => {
  const {
    sites,
    getSite,
    getOrganization,
    organizations,
    isLoading,
    hasEverLoaded,
    forceRefresh,
  } = useRolodexEntries({
    type: [RolodexEntryType.SITE, RolodexEntryType.ORGANIZATION],
    order_by: [{ key: 'name', descending: 'false' }],
  })

  const { site, sitesForOrg, organization } = useMemo(() => {
    const site = props.siteID
      ? // If there's a site ID in the URL, find that site
        getSite(props.siteID)
      : // Otherwise, redirect to the demo site if the user has access to
        // the demo and the first available site if the user doesn't
        (getSite('atlantic-deer-island') ?? sites.at(0))

    // If we didn't get a site based on the ID prop, assume it's an organization ID
    const organization = props.orgID
      ? getOrganization(props.orgID)
      : site
        ? getOrganization(site.tags.organization)
        : getOrganization(props.siteID)
    const sitesForOrg = sites.filter(
      (s) => s.tags.organization === organization?.id
    )
    return { site, sitesForOrg, organization }
  }, [props.siteID, props.orgID, getSite, sites, getOrganization])

  // TODO: ENT-1266 -- Remove this when we are able to optimistically update
  // Goal here is to force refresh the data in the ForecastTopBar when a new Organization or Site is added
  // Since the Landing page (and useLandingPageData) exists as a child of the drawer, we have to react to
  // changes in state of the drawer itself. To prevent re-fetching the data every re-render that uses useLandingPageData
  // we set a state that says "the edit organization / site drawer was open", and "when it closes, refresh the data"
  const [forceRefreshData, setForceRefreshData] = useState(false)
  const { drawerState } = useRightDrawer()

  useEffect(() => {
    // A state of interest has been opened, let's prep the refresh
    if (
      (drawerState === DrawerState.EditGeckoOrganization ||
        drawerState === DrawerState.EditGeckoSite) &&
      !forceRefreshData
    ) {
      setForceRefreshData(true)
    }
    // The drawer has now been closed (after being in a state of interest), refresh the data and reset the variable
    if (drawerState === DrawerState.Closed && forceRefreshData) {
      forceRefresh()
      setForceRefreshData(false)
    }
  }, [drawerState, forceRefreshData, forceRefresh])

  return {
    organization,
    organizations,
    site,
    sites,
    sitesForOrg,
    isLoading,
    hasEverLoaded,
    forceRefresh,
  }
}
